import { Box, Typography, TextField, Button, CircularProgress } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { primaryColor } from "utils";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { AmountType } from "utils/types/PricingTypes";
import { convertCurrencyAPI } from "utils/api/public/PricingAPIservices";

interface ConvertorParams {
  selectedCountry: {
    currencyCode: string;
    currencySymbol: string;
  };
}

// Defines the structure for the amounts in the converter's state, allowing strings for input fields
interface Amounts {
  bsbCoinAmount: number | string;
  usdAmount: number | string;
  localCurrencyAmount: number | string;
}

export const Convertor: React.FC<ConvertorParams> = ({ selectedCountry }) => {
  const [amounts, setAmounts] = useState<Amounts>({
    bsbCoinAmount: "",
    usdAmount: "",
    localCurrencyAmount: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastEditedField = useRef<AmountType | null>(null);

  // **MODIFIED**: This hook now loads data from localStorage based on the selected country
  useEffect(() => {
    // Define dynamic keys based on the current country's currency code
    const localStorageBsbKey = `bsbCoinAmount_${selectedCountry.currencyCode}`;
    const localStorageUSDKey = `usdAmount_${selectedCountry.currencyCode}`;
    const localStorageLocalKey = `localCurrencyAmount_${selectedCountry.currencyCode}`;

    // Load saved values for the CURRENT country
    const savedBsbCoin = localStorage.getItem(localStorageBsbKey) || "";
    const savedUsd = localStorage.getItem(localStorageUSDKey) || "";
    const savedLocal = localStorage.getItem(localStorageLocalKey) || "";
    
    setAmounts({
      bsbCoinAmount: savedBsbCoin,
      usdAmount: savedUsd,
      localCurrencyAmount: savedLocal,
    });

    // Reset other state when country changes
    lastEditedField.current = null;
    setError(null);
  }, [selectedCountry]);

  const handleConvert = async () => {
    if (!lastEditedField.current) {
      setError("Please enter a value to convert.");
      return;
    }

    const amountType = lastEditedField.current;
    const amountValue = amounts[amountType];

    if (amountValue === "" || Number(amountValue) <= 0) {
      setError("Please enter a positive value.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await convertCurrencyAPI(
        amountType,
        Number(amountValue),
        selectedCountry.currencyCode
      );
      
      if (response.status === "success" && response.data) {
        setAmounts(response.data);

        // Define dynamic keys again for saving
        const localStorageBsbKey = `bsbCoinAmount_${selectedCountry.currencyCode}`;
        const localStorageUSDKey = `usdAmount_${selectedCountry.currencyCode}`;
        const localStorageLocalKey = `localCurrencyAmount_${selectedCountry.currencyCode}`;

        // Save to localStorage with the country-specific key
        localStorage.setItem(localStorageBsbKey, response.data.bsbCoinAmount.toString());
        localStorage.setItem(localStorageUSDKey, response.data.usdAmount.toString());
        localStorage.setItem(localStorageLocalKey, response.data.localCurrencyAmount.toString());

      } else {
        const errorMessage = response.message || "An unknown error occurred.";
        setError(errorMessage);
        console.error("Error in conversion:", errorMessage);
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to handle conversion request.";
      setError(errorMessage);
      console.error("Error handling conversion:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field: AmountType, value: string) => {
    setAmounts({
      bsbCoinAmount: field === "bsbCoinAmount" ? value : "",
      usdAmount: field === "usdAmount" ? value : "",
      localCurrencyAmount: field === "localCurrencyAmount" ? value : "",
    });
    lastEditedField.current = field;
    setError(null);
  };

  return (
    <Box 
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        border: "1px solid #e0e0e0", 
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        maxWidth: 500,
        mx: 'auto'
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          alignItems: 'center', 
          gap: 2 
        }}
      >
        <TextField
          label="BSB COINS"
          value={amounts.bsbCoinAmount}
          onChange={(e) => handleInputChange("bsbCoinAmount", e.target.value)}
          variant="outlined"
          type="number"
          inputProps={{ min: 0 }}
          fullWidth
        />
        <SwapVertIcon sx={{ color: 'grey.500', transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
        <TextField
          label="USD"
          value={amounts.usdAmount}
          onChange={(e) => handleInputChange("usdAmount", e.target.value)}
          type="number"
          inputProps={{ min: 0 }}
          variant="outlined"
          fullWidth
        />
        <SwapVertIcon sx={{ color: 'grey.500', transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
        <TextField
          label={`${selectedCountry.currencyCode}`}
          value={amounts.localCurrencyAmount}
          onChange={(e) => handleInputChange("localCurrencyAmount", e.target.value)}
          type="number"
          inputProps={{ min: 0 }}
          variant="outlined"
          fullWidth
        />
      </Box>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: primaryColor, width: { xs: '100%', sm: 'auto' } }}
          onClick={handleConvert}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Confirmer"}
        </Button>
        {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
            </Typography>
        )}
      </Box>
    </Box>
  );
};

